import { useState } from 'react'
import { colors, font, text, space, radius } from '../design/tokens'
import { salidasApi } from '../api/salidas'
import Boton from './Boton'

/**
 * Freno "entrega antes de la fecha pactada" (Martin, 30 oct 2026): un envío
 * con fecha pactada no sale antes sin el OK del vendedor que procesó la venta.
 * Lista los envíos frenados y, por cada uno, el botón para pedirle la
 * autorización (le llega el aviso en LGR y por Telegram si lo tiene).
 *
 * `frenos` = el array `entrega_anticipada` que devuelve el backend.
 */
export default function FrenoEntregaAnticipada({ frenos }) {
  const [pedidos, setPedidos] = useState({})
  const [enviando, setEnviando] = useState(null)

  if (!frenos?.length) return null

  const pedir = async (f) => {
    const k = `${f.entidad_tipo}-${f.entidad_id}`
    setEnviando(k)
    try {
      const { data } = await salidasApi.pedirAnticipada({ entidad_tipo: f.entidad_tipo, entidad_id: f.entidad_id })
      setPedidos(p => ({ ...p, [k]: { ok: true, texto: data.message } }))
    } catch (e) {
      setPedidos(p => ({ ...p, [k]: { ok: false, texto: e.response?.data?.message || 'No se pudo pedir la autorización.' } }))
    } finally {
      setEnviando(null)
    }
  }

  return (
    <div style={st.box}>
      <div style={st.titulo}>📅 Entrega antes de la fecha pactada con el cliente</div>
      <div style={st.bajada}>
        No se puede entregar antes de lo acordado sin el OK del vendedor que procesó la venta. Después de esa fecha, sí.
        Sacá {frenos.length === 1 ? 'este envío' : 'estos envíos'} de la salida o pedí la autorización.
      </div>
      {frenos.map(f => {
        const k = `${f.entidad_tipo}-${f.entidad_id}`
        const pedido = pedidos[k]
        const quien = f.responsable || 'administración'
        return (
          <div key={k} style={st.fila}>
            <span style={st.numero}>{f.numero}</span>
            <span style={st.pactada}>pactada <span style={st.fecha}>{f.fecha_pactada}</span></span>
            <span style={st.accion}>
              {pedido?.ok || (f.pendiente && !pedido)
                ? <span style={st.chip}>⏳ Pedida a {quien}</span>
                : f.rechazada && !pedido
                  ? <span style={{ ...st.chip, color: colors.warn }}>✕ {quien} no la autorizó</span>
                  : <Boton variante="send" sm disabled={enviando === k} onClick={() => pedir(f)}>
                      {enviando === k ? 'Pidiendo…' : `→ Pedir autorización a ${quien}`}
                    </Boton>}
            </span>
            {pedido && <div style={pedido.ok ? st.ok : st.err}>{pedido.texto}</div>}
          </div>
        )
      })}
    </div>
  )
}

const st = {
  box: { background: colors.warnBg, border: `1px solid ${colors.warnBorder}`, borderRadius: radius.md, padding: space.lg, marginBottom: space.lg },
  titulo: { fontSize: text.body, fontWeight: 600, color: colors.warn, marginBottom: space.xs },
  bajada: { fontSize: text.sec, color: colors.warn, marginBottom: space.md },
  fila: { display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: space.md, padding: `${space.sm}px 0`, borderTop: `1px solid ${colors.warnBorder}` },
  numero: { fontFamily: font.mono, fontVariantNumeric: 'tabular-nums', fontSize: text.body, color: colors.text },
  pactada: { fontSize: text.sec, color: colors.textSec },
  fecha: { fontFamily: font.mono, fontVariantNumeric: 'tabular-nums' },
  accion: { marginLeft: 'auto' },
  chip: { fontSize: text.sec, fontWeight: 600, color: colors.textSec, background: colors.bg, border: `1px solid ${colors.border}`, borderRadius: radius.xs, padding: `2px ${space.md}px` },
  ok: { width: '100%', fontSize: text.sec, color: colors.ok },
  err: { width: '100%', fontSize: text.sec, color: colors.err },
}

/**
 * La marca de la grilla de Asignación (Martin, 31 oct 2026: "eso lo tiene que
 * ver antes de trabajar sin sentido"): un envío pactado para más adelante dice,
 * en su fila, que no sale antes sin el OK del vendedor y en qué quedó el
 * pedido — con el botón para pedirlo ahí mismo, antes de armar nada.
 *
 * `info` = el `entrega_anticipada` que el backend cuelga de cada candidato.
 */
export function ChipEntregaAnticipada({ info }) {
  const [estado, setEstado] = useState(info?.estado ?? null)
  const [enviando, setEnviando] = useState(false)
  const [error, setError] = useState('')
  if (!info) return null
  const quien = info.responsable || 'administración'

  const pedir = async (e) => {
    e.stopPropagation()
    setEnviando(true); setError('')
    try {
      await salidasApi.pedirAnticipada({ entidad_tipo: info.entidad_tipo, entidad_id: info.entidad_id })
      setEstado('pendiente')
    } catch (err) {
      setError(err.response?.data?.message || 'No se pudo pedir.')
    } finally { setEnviando(false) }
  }

  if (estado === 'aprobada') return <div style={stChip.ok} title={`${quien} autorizó entregarlo antes de la fecha pactada`}>✓ OK de {quien} para antes</div>
  return (
    <div style={stChip.wrap} onClick={e => e.stopPropagation()}>
      <div style={stChip.aviso} title={`Pactada con el cliente para el ${info.fecha_pactada}: antes de esa fecha no sale sin el OK de ${quien}`}>
        📅 No sale antes sin OK del vendedor
      </div>
      {estado === 'pendiente'
        ? <span style={stChip.gris}>⏳ Pedido a {quien}</span>
        : estado === 'rechazada'
          ? <span style={{ ...stChip.gris, color: colors.warn }}>✕ {quien} no lo autorizó</span>
          : <Boton variante="send" sm disabled={enviando} onClick={pedir}>{enviando ? 'Pidiendo…' : `→ Pedir OK a ${quien}`}</Boton>}
      {error && <div style={{ fontSize: text.meta, color: colors.err }}>{error}</div>}
    </div>
  )
}

const stChip = {
  wrap: { display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: space.xs, marginTop: space.xs, fontFamily: font.sans },
  aviso: { fontSize: text.meta, fontWeight: 600, color: colors.warn, background: colors.warnBg, border: `1px solid ${colors.warnBorder}`, borderRadius: radius.xs, padding: `1px ${space.sm}px`, whiteSpace: 'nowrap' },
  gris: { fontSize: text.meta, fontWeight: 600, color: colors.textSec, background: colors.bg, border: `1px solid ${colors.border}`, borderRadius: radius.xs, padding: `1px ${space.sm}px`, whiteSpace: 'nowrap' },
  ok: { marginTop: space.xs, fontSize: text.meta, fontWeight: 600, color: colors.ok, background: colors.okBg, borderRadius: radius.xs, padding: `1px ${space.sm}px`, whiteSpace: 'nowrap', fontFamily: font.sans },
}
